/**
 * ScreenerGrid — the grid island. Binds a decoded screener snapshot to AG Grid
 * Community, driven entirely by the contract's `ScreenerConfig`.
 *
 * Three things carry the design:
 *
 * 1. **`getRowId`.** The server re-pushes a *full snapshot* on every refresh,
 *    never a delta. Without a stable row id AG Grid tears down and rebuilds
 *    every row on each push, destroying selection, scroll position and focus.
 *    With one, it diffs.
 * 2. **`applyTransactionAsync`.** Refreshes go through the async transaction
 *    queue, which coalesces internally — the ">= 250 ms batched" rule from D18 —
 *    instead of re-setting `rowData` and re-running the whole render.
 * 3. **State flows back out contract-shaped.** Filter, sort and column changes
 *    are emitted as `ScreenerConfig` fragments, so the workspace — not the grid —
 *    remains the source of truth and the same state replays on any renderer.
 *
 * Community only: no Set Filter, no row grouping, no `aggFunc`. Rollups arrive
 * pre-aggregated from the server as their own frames.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
  CellEditingStoppedEvent,
  CellMouseOutEvent,
  CellMouseOverEvent,
  ColDef,
  FilterChangedEvent,
  GetRowIdParams,
  GridReadyEvent,
  GridApi,
  ICellRendererParams,
  IRowNode,
  SortChangedEvent,
} from "ag-grid-community";
import type {
  ScreenerConfig,
  ScreenerExpr,
  ScreenerFilterNode,
  ScreenerSort,
} from "@qufolio/model";
import type { ScreenerColumnMeta, ScreenerFrame, ScreenerRow } from "@qufolio/transport";

import { TimeframeHeader } from "./TimeframeHeader.js";
import { TrackListCell, type TrackMark } from "./TrackListCell.js";
import { ColumnStateCell } from "./ColumnStateCell.js";
import { AddRowCell, type AddRowCellParams } from "./AddRowCell.js";
import { AddRowEditor, type AddRowEditorParams } from "./AddRowEditor.js";
import { ImageCell } from "./ImageCell.js";
import { SymbolSearchBox, type SymbolSearchBoxParams } from "./SymbolSearchBox.js";

import {
  ADD_ROW_ID,
  ADD_ROW_PLACEHOLDER,
  addRowData,
  addRowOutcome,
  addRowSuppressesKey,
  isAddRowNode,
  type AddRowBinding,
} from "../lib/add-row.js";
import { isUserOriginated } from "../lib/event-source.js";
import { onFitRequest } from "../lib/fit-request.js";
import { createSymbolSearch, matchesSymbolSearch } from "../lib/symbol-search.js";
import { buildUniverse, type ColumnOrigin } from "../lib/column-universe.js";
import {
  rowCursorStep,
  stepRowIndex,
  type RowStep,
} from "../lib/row-cursor.js";
import { evaluate, needsHistory } from "../lib/expr.js";
import {
  buildColumnDefs,
  isEventColumn,
  columnsFromDefs,
  trackColumnIndex,
  TRACK_LIST_FIELD,
  type TokenResolver,
} from "../lib/columns.js";
import { groupIntoBands, leafDefs, LIVE_WINDOW_CLASS, type Band, type CategoryOf } from "../lib/bands.js";
import { BandHeader, BAND_STYLE } from "./BandHeader.js";
import { themeCssVars } from "../lib/theme.js";
import { isPlainTypingKey } from "../lib/typing-keys.js";
import { fromGridModel, type GridFilterModel } from "../lib/filter-ast.js";
import { diffRows, resolveRowIdField, rowId } from "../lib/row-diff.js";
import { matchesFilter, type ScreenerFilter } from "../lib/filter-eval.js";
import type { NewsOpen } from "./NewsCell.js";

/**
 * A contract-shaped fragment of grid state, ready to merge into the workspace.
 *
 * `savedFilters` is deliberately absent: the filter *library* is a workspace-level
 * concern, so mutating it is a separate callback rather than a section patch.
 * (§1 of the DSL plan moves it to `UserWorkspace` outright; until then it lives on
 * the section but is still edited as a library, not as section state.)
 */
export type ScreenerConfigPatch = Partial<
  Pick<
    ScreenerConfig,
    | "columns"
    | "filter"
    | "sort"
    | "activeFilterRef"
    | "activeFilterRefs"
    // The header marks' live window — the Columns tab's Pre/post tick.
    | "extendedHours"
    // Which saved screen the panel is showing. A patch rather than its own
    // action because it travels the same path as every other change to this
    // section — one save, one undo, and no second route for the host to wire.
    | "screenRef"
    // The conditions picker's category order, moved with ↑↓ on a group header.
    | "savedFilterCategoryOrder"
    // A JP trade-list screen's bar and side — the host's `RecsBar`.
    | "recs"
    // A TopDown screen: its ticks, layout and level columns (the host's panel).
    | "topDown"
    // A Seasonality screen: its bar (the host's panel).
    | "seasonality"
    // The bar's ▲▼ key: All / gainers / losers (docs/plans/movers.md).
    | "direction"
  >
>;


/** Module-level so the identity is stable — a new object each render would
 * make AG Grid rebuild every header. */
const HEADER_COMPONENTS = { agColumnHeader: TimeframeHeader };

/**
 * The two no-value states, as `cellRendererParams`.
 *
 * Module-level for `HEADER_COMPONENTS`' reason and `TrackListBinding.version`'s:
 * AG Grid compares `cellRendererParams` by identity, so a fresh `{ state }`
 * object per render would rebuild the column definitions and, through them, the
 * grid — on every tick of a screen that has one of these columns.
 */
const PENDING_PARAMS = { state: "pending" } as const;
const UNAVAILABLE_PARAMS = { state: "unavailable" } as const;

/** Said in the header too, since the reason is a fact about the column. */
const STATE_TOOLTIP = {
  pending: "Waiting for this column's first values.",
  unavailable: "No service is computing this column — nothing is going to send it.",
} as const;

/**
 * The star column's two hover rules, as CSS rather than as React state.
 *
 * A hover handled in React is a re-render per row per mouse move across the
 * grid, on a surface that already re-renders on a 2-second server push. AG Grid
 * puts `ag-row-hover` on the row element, so the rule is one selector and the
 * cell never re-renders at all.
 *
 * Scoped under `.qf-screener-island` so it cannot reach a second grid — or
 * anything else — on a page that hosts more than one.
 */
const TRACK_STYLE = `
.qf-screener-island .qf-track-empty { opacity: .2; transition: opacity .08s; }
.qf-screener-island .ag-row-hover .qf-track-empty { opacity: .85; }
.qf-screener-island .qf-track-cell:hover .qf-track-empty { opacity: 1; }
`;

/**
 * The closed search row, hidden as well as flattened.
 *
 * Zero height already clips it — AG Grid's header rows are `overflow: hidden` —
 * but a zero-height cell can still take focus, and AG Grid's header keyboard
 * navigation will step into it. `visibility: hidden` makes it unfocusable, so a
 * closed search is not a place the keyboard can get lost in.
 */
const SEARCH_STYLE = `
.qf-screener-island[data-symbol-search="closed"] .ag-header-row-column-filter { visibility: hidden; }
`;

/**
 * The Add row's hint runs past its cell. `+ Add or paste symbols…` is wider than
 * a ticker column, and the cells beside it on that row are empty whenever the
 * hint shows — the message cell only holds text while the box is open, and an
 * open box replaces the hint.
 */
const ADD_ROW_STYLE = `
.qf-screener-island .ag-cell.qf-add-row-hint { overflow: visible; z-index: 1; }
`;

/**
 * What the host must supply for the star column to exist at all.
 *
 * Omitted, and no column is injected — which is what a rollup tab wants, and
 * what any host that has not built track lists gets.
 */
export interface TrackListBinding {
  /** This symbol's membership, as colours. Empty array for "in no list". */
  marks: (symbol: string) => readonly TrackMark[];
  /**
   * A star was clicked. `symbols` is the row clicked, or the whole selection
   * when the clicked row is inside it — the file-manager rule, decided here
   * because only the grid knows what is selected.
   */
  onOpen: (symbols: readonly string[], anchor: DOMRect, alt: boolean) => void;
  /**
   * Bumped whenever membership changes anywhere.
   *
   * The column carries no field, so its cells have no value to change and AG
   * Grid will never refresh them on its own — a star would stay hollow after
   * the add that filled it, until something unrelated forced a redraw. This is
   * the signal to repaint, and it is a number rather than the membership itself
   * so that passing it cannot rebuild the column definitions.
   */
  version: number;
}

// ── the keyboard row cursor ─────────────────────────────────────────────────
//
// Walking a screen row by row and watching each chart in turn is the reason the
// screener and the chart sit on the same page, and a mouse makes it a click per
// symbol. So ↓/Space step to the next *displayed* row and ↑ to the previous,
// selecting what they land on — selection being the existing route to
// `onSelectSymbol`, so a keyboard move re-charts by exactly the path a click does.
//
// The three decisions below are pure functions rather than branches inside the
// handler because each of them has an edge that is invisible in a browser and
// obvious in a test. They would live in `lib/` beside `isUserOriginated` — a
// module exporting both a component and plain functions cannot be hot-reloaded —
// but they are exported here so the seam is reachable without rendering AG Grid,
// which jsdom cannot lay out.

/** One row down (`1`) or one row up (`-1`). */

/** Where a hovered Symbol cell is: its box and the grid's, in viewport pixels. */
export interface SymbolHoverBox {
  cell: DOMRect;
  grid: DOMRect;
}

export interface ScreenerGridProps {
  /** The frame to render — `detail`, or one of the server-computed rollups. */
  frame: ScreenerFrame | undefined;
  /**
   * How many rows are showing after filtering, and how many arrived.
   *
   * Reported from the grid because the grid is what applied the filter — the
   * config holds the *rule*, and counting rows against it a second time
   * elsewhere would be a second evaluator to keep in step.
   */
  onRowCounts?: (matched: number, total: number) => void;
  /**
   * A row was selected — the symbol it identifies.
   *
   * The screener's whole point is picking something to look at, so selecting a
   * row is how a chart gets told what to show. Reported as the id value rather
   * than the row, because that is the one field every consumer of this can be
   * sure means the same thing.
   */
  onSelectSymbol?: (symbol: string) => void;
  /**
   * The selection changed, as every selected symbol.
   *
   * Separate from `onSelectSymbol` rather than an overload of it, because the
   * two answer different questions and one of them must not start lying:
   * `onSelectSymbol` means *show me this one* and fires only at exactly one
   * selected row. A second row being ctrl-clicked is not a request to chart
   * anything, and reporting it through the same callback would make the chart
   * follow the last row of a bulk selection.
   */
  onSelectedSymbols?: (symbols: readonly string[]) => void;
  /**
   * The pointer entered a row's Symbol cell (the cell's box and the grid's, in
   * viewport pixels), or left it (`null`). The host decides what to show and when;
   * this only reports. Never fired for the pinned Add row or a rollup row.
   */
  onHoverSymbol?: (symbol: string | null, at: SymbolHoverBox | null) => void;
  /** Track lists: the star column. Omitted ⇒ no column is injected. */
  track?: TrackListBinding;
  /**
   * A click on a Movers News cell: the host opens the window's headlines
   * (`components/NewsCell.tsx`). Omitted ⇒ the cell is not clickable.
   */
  onNewsOpen?: NewsOpen;
  /**
   * The Add row: an empty row pinned last, whose Symbol cell takes a ticker.
   * Omitted ⇒ no row, and the grid is exactly what it was. See `lib/add-row.ts`
   * for why it is pinned rather than a row in the data.
   */
  addRow?: AddRowBinding;
  /**
   * What the Symbol column's search is searching — the screen and the universe,
   * as any string that changes when either does. A change closes and empties
   * the search: a filter typed against one group must not silently thin out the
   * next one. Omitted, the search lasts as long as the grid.
   */
  searchScope?: string;
  /** The contract config driving columns, filter and sort. */
  config: ScreenerConfig;
  /** Per-column metadata from qv-data: labels, friendly names, timeframes. */
  meta?: readonly ScreenerColumnMeta[];
  /**
   * What the server acknowledged serving, per column — `readColumnSources` over
   * the same frames.
   *
   * Only ever *narrows* what is drawn as waiting: a computed column the server
   * did not acknowledge is nobody's to send, so it says "no source" instead of
   * spinning forever. Omitted, nothing is acknowledged — which is the correct
   * reading of a server that predates the field, and is every server until a
   * compute service exists.
   */
  sources?: ReadonlyMap<string, ColumnOrigin>;
  /**
   * Field uniquely identifying a row. Defaults to `symbol` for detail frames and
   * falls back to the frame's first text field, which is the group key on a
   * rollup frame.
   */
  rowIdField?: string;
  /**
   * Resolves semantic color tokens (D13) to CSS colors — for conditional
   * formatting *and* for the grid's own chrome, which is published as AG Grid's
   * `--ag-*` variables. One resolver, so a cell's green and the grid it sits in
   * can never come from different themes.
   */
  resolveToken?: TokenResolver;
  /**
   * BCP 47 for the market this screen is showing, used only by `compact`
   * notation — an Indian screen then reads in lakh and crore rather than K/M/B.
   *
   * One per grid rather than one per row, because a screener shows one market at
   * a time (D40): `marketCap` is native currency and `close_spy` is the market's
   * own benchmark, so a mixed screen has no coherent column to sort. Omitted
   * behaves exactly as before.
   */
  locale?: string;
  /** Grid state changed — persist the patch into the workspace. */
  onConfigChange?: (patch: ScreenerConfigPatch) => void;
  /**
   * The transient column-funnel layer changed. Reported, never persisted — see
   * `handleFilterChanged`. The filter bar shows its count and offers to merge it
   * into the base filter.
   */
  onAdHocChange?: (adHoc: ScreenerFilterNode | undefined) => void;
  /** AG Grid theme class. Defaults to the Quartz dark theme. */
  themeClass?: string;
  /**
   * The market is live now — the host decides, from its clock, its calendar
   * and the screen's `extendedHours`. Live and computed headers are marked
   * only while it is (docs/plans/screener-bands.md).
   */
  liveWindow?: boolean;
  /**
   * Size to the rows instead of filling the host — a Strat list's handful of
   * funds, with its strip right underneath (owner, 2026-10-27). Every row is
   * rendered, so only for short frames.
   */
  autoHeight?: boolean;
  /** An indicator's catalog category, for a computed column's default band. */
  categoryOf?: CategoryOf;
  /**
   * The grid's rows as they stand — filtered and sorted — for the host's Export.
   *
   * Handed out rather than re-derived by the host for `onRowCounts`' reason:
   * the grid is what applied the filter and the sort, and which columns are
   * showing, in what order, is the grid's state, not the config's. `null` when
   * the grid goes away.
   */
  onExporter?: (exporter: ScreenerExporter | null) => void;
}

/** What `onExporter` hands the host. Read at call time, so always current. */
export interface ScreenerExporter {
  /** Each row on screen, raw values by field, in the grid's order. */
  rows: () => ScreenerRow[];
  /**
   * Every shown column as CSV, headers as displayed and values as formatted.
   * The star column and image columns are left out — a colour slot and a URL
   * are not data anybody exported a screen to get.
   */
  csv: () => string;
  /**
   * One row as the grid shows it: every displayed column's id, header text and
   * raw value (a client column's computed one), in display order — what Train
   * freezes with a case (`docs/plans/quevee-voice.md`). The same columns as
   * `csv`. `undefined` when the row is not in the grid; a filtered-out row is
   * still read, since the grid holds it.
   */
  rowOf: (id: string) => ExportedCell[] | undefined;
}

/** One cell of `ScreenerExporter.rowOf`. */
export interface ExportedCell {
  field: string;
  header: string;
  value: unknown;
}

export function ScreenerGrid({
  frame,
  config,
  meta,
  sources,
  rowIdField,
  resolveToken,
  locale,
  onConfigChange,
  onAdHocChange,
  themeClass = "ag-theme-quartz-dark",
  onRowCounts,
  onSelectSymbol,
  onHoverSymbol,
  onSelectedSymbols,
  track,
  onNewsOpen,
  addRow,
  searchScope,
  liveWindow = false,
  autoHeight = false,
  categoryOf,
  onExporter,
}: ScreenerGridProps) {
  const apiRef = useRef<GridApi<ScreenerRow> | null>(null);

  // AG Grid's React wrapper initialises asynchronously, so `onGridReady` can
  // fire *after* this component's mount effects. Effects that touch the api
  // must therefore depend on this flag rather than just reading the ref — a
  // miss means rowData is never applied and the grid sits on its built-in
  // "Loading…" overlay until some unrelated dependency happens to change.
  const [ready, setReady] = useState(false);

  // Rows already in the grid, so a refresh can be diffed into add/update/remove
  // rather than replacing everything.
  const presentIds = useRef<Set<string>>(new Set());

  const idField = useMemo(
    () => resolveRowIdField(frame?.fields ?? [], rowIdField),
    [frame?.fields, rowIdField],
  );

  // AG Grid's `context`, read by the News cell. The callback goes through a ref
  // so a host passing a new arrow each render does not hand AG Grid a new
  // context — and no ref at all means the cell is not clickable.
  const newsOpenRef = useRef(onNewsOpen);
  newsOpenRef.current = onNewsOpen;
  const hasNewsOpen = onNewsOpen !== undefined;
  const gridContext = useMemo(
    () => ({
      idField: idField ?? undefined,
      onNewsOpen: hasNewsOpen
        ? (...args: Parameters<NewsOpen>) => newsOpenRef.current?.(...args)
        : undefined,
    }),
    [idField, hasNewsOpen],
  );

  // Keyed on a *value* signature, not array identity. Writing grid state back to
  // the workspace produces a new `columns` array on every change; if that
  // identity drove this memo, each write would rebuild the columns, which fires
  // the column events again, which writes again — an unbreakable loop. Comparing
  // by value means an equal-but-new array is a no-op.
  const columnsKey = useMemo(() => JSON.stringify(config.columns ?? []), [config.columns]);
  const fieldsKey = useMemo(
    () => (frame?.fields ?? []).map((f) => `${f.name}:${f.type}`).join(","),
    [frame?.fields],
  );
  // Keyed by value for exactly `columnsKey`'s reason, and it matters more here:
  // the acknowledgement map is rebuilt from every arriving payload, so a memo
  // depending on its *identity* would rebuild every column definition — and
  // through them the whole grid — on each push. What it says almost never
  // changes; the object holding it always does.
  const sourcesKey = useMemo(
    () => (sources ? [...sources].map(([field, source]) => `${field}:${source}`).join(",") : ""),
    [sources],
  );

  // Held in a ref, not read in the memo below. `track` is a fresh object every
  // render — its `marks` closes over the host's list state — so depending on it
  // would rebuild the column definitions on each render and, through them, the
  // grid. The column is injected once; what it reads changes underneath it.
  const trackRef = useRef(track);
  trackRef.current = track;

  /**
   * The number columns that hold only -1, 0 or 1 — events and flags such as
   * Bongo — which print without decimals (owner, 2026-10-06). Learned from the
   * data on every frame and read by the formatter through a stable lookup, so
   * knowing it never rebuilds the column definitions (and with them the grid);
   * a change only refreshes those columns' cells.
   */
  const eventFields = useRef<ReadonlySet<string>>(new Set());
  const wholeNumber = useCallback((field: string) => eventFields.current.has(field), []);
  // The *presence* of a binding is what decides whether the column exists, and
  // it is a boolean so the memo below can depend on it without depending on the
  // object.
  const hasTrack = track != null;

  /**
   * Stable params for the star cell.
   *
   * Everything the cell needs is reached through `trackRef`, so this object's
   * identity depends only on which field is the symbol. That is the whole trick:
   * AG Grid compares `cellRendererParams` by identity, and a new object means a
   * new column.
   */
  const trackParams = useMemo(
    () => ({
      idField: idField ?? undefined,
      getMarks: (symbol: string) => trackRef.current?.marks(symbol) ?? [],
      onOpen: (symbol: string, anchor: DOMRect, alt: boolean) => {
        const api = apiRef.current;
        const binding = trackRef.current;
        if (!binding) return;
        // **The file-manager rule, and the grid is the only thing that can
        // apply it**: a star inside the selection acts on the selection, one
        // outside it acts on that row alone. Decided here rather than by the
        // host because `getSelectedRows` is not something the host can see
        // without the grid handing it over on every click.
        const selected = (api?.getSelectedRows() ?? [])
          .map((row) => (idField ? row[idField] : undefined))
          .filter((value): value is string => typeof value === "string" && value !== "");
        const scope = selected.includes(symbol) && selected.length > 1 ? selected : [symbol];
        binding.onOpen(scope, anchor, alt);
      },
    }),
    [idField],
  );

  // ── the Add row ────────────────────────────────────────────────────────────
  //
  // `trackRef`'s shape, for its reason: the host rebuilds the binding on every
  // render, and anything the column definitions hold must not change identity
  // with it or the grid is rebuilt under the editor. Presence is the boolean the
  // memos depend on; everything else is read through the ref at call time.
  const addRowRef = useRef(addRow);
  addRowRef.current = addRow;
  const hasAddRow = addRow != null;

  /**
   * The last refusal, drawn in the row's message cell. State rather than a ref
   * because it *is* what the row renders: it rides on the pinned row's data, and
   * new data is what makes AG Grid redraw a row's cells.
   */
  const [addMessage, setAddMessage] = useState<string | null>(null);

  // A different screen is a different question; a refusal about the last one
  // must not be waiting on the next list's row.
  useEffect(() => {
    if (!hasAddRow) setAddMessage(null);
  }, [hasAddRow]);

  const addCellParams = useMemo<AddRowCellParams | undefined>(
    () =>
      idField
        ? {
            symbolField: idField,
            placeholder: () => addRowRef.current?.placeholder ?? ADD_ROW_PLACEHOLDER,
          }
        : undefined,
    [idField],
  );

  /** Stable for `trackParams`' reason: AG Grid holds editor params by identity. */
  const addEditorParams = useMemo<AddRowEditorParams>(
    () => ({
      suggest: (text, caret) => addRowRef.current?.suggest(text, caret) ?? [],
      submit: async (text) => {
        const binding = addRowRef.current;
        if (!binding) return { remaining: text, message: null };
        let outcome: { remaining: string; message: string | null };
        try {
          outcome = addRowOutcome(await binding.onAdd(text));
        } catch (error) {
          // A host that throws instead of reporting still gets its sentence on
          // screen, and the text stays in the box to try again.
          outcome = { remaining: text, message: error instanceof Error ? error.message : String(error) };
        }
        // Only onto a row still being typed into. An add that resolves after
        // Escape would otherwise leave a sentence about text nobody can see.
        const editing =
          apiRef.current?.getEditingCells().some((cell) => cell.rowPinned === "bottom") ?? false;
        setAddMessage(editing ? outcome.message : null);
        return outcome;
      },
      // A no-op when there is nothing to clear, so typing costs no render.
      clearMessage: () => setAddMessage((held) => (held === null ? held : null)),
      placeholder: () => addRowRef.current?.placeholder ?? ADD_ROW_PLACEHOLDER,
      // The island root, declared below with the row cursor. Read at call time,
      // which is always after the first render has set it.
      portal: () => rootRef.current,
      takeSeed: () => {
        const seed = pastedSeed.current;
        pastedSeed.current = null;
        return seed;
      },
    }),
    [],
  );

  /**
   * A paste onto the focused Add cell, waiting for the editor it opens.
   *
   * AG Grid starts an edit on a typed letter and hands the editor that letter as
   * `eventKey`; Ctrl/⌘+V is not a letter, so before this a paste onto the row
   * went nowhere — the box only took one once somebody had clicked into it. The
   * paste is read here, the edit started, and the editor takes the text once.
   */
  const pastedSeed = useRef<string | null>(null);

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      const api = apiRef.current;
      if (!api || !addRowRef.current || !idField) return;
      // The cell the paste landed on — the focused element, which for a cell
      // not being edited is the cell itself. An open editor's input handles
      // its own paste and is excluded by `getEditingCells`.
      const cell = (event.target as HTMLElement).closest?.(".ag-floating-bottom .ag-cell");
      if (!cell || cell.getAttribute("col-id") !== idField) return;
      if (api.getEditingCells().length > 0) return;
      const text = event.clipboardData.getData("text").trim();
      if (!text) return;
      event.preventDefault();
      pastedSeed.current = text;
      api.startEditingCell({ rowIndex: 0, rowPinned: "bottom", colKey: idField });
    },
    [idField],
  );

  // ── the Symbol column's search ─────────────────────────────────────────────
  //
  // One store per grid, shared by the header's 🔍, the box in the filter row
  // and the filter pass below — see `lib/symbol-search.ts`. Offered only where
  // the rows are symbols: a rollup's key is a sector, and its tab has no Symbol
  // column to hang a box under.
  const [search] = useState(createSymbolSearch);
  const searchable = idField === "symbol";

  /**
   * Whether the row is showing — the one part of the search this component
   * renders from, and it changes on a click, not on a keystroke. The text is
   * read through `searchText` instead, so typing re-runs the filter without
   * re-rendering the grid around it.
   */
  const searchOpen = useSyncExternalStore(search.subscribe, () => search.get().open);

  /** Stable, for `trackParams`' reason. */
  const searchParams = useMemo<SymbolSearchBoxParams>(() => ({ symbolSearch: search }), [search]);

  // A new screen or a new universe is a new question: the search closes, empty.
  const seenScope = useRef(searchScope);
  useEffect(() => {
    if (seenScope.current === searchScope) return;
    seenScope.current = searchScope;
    search.reset();
  }, [search, searchScope]);

  const columnDefs = useMemo<ColDef<ScreenerRow>[]>(() => {
    /**
     * The search's half of the Symbol column: a floating filter that is the box,
     * and the 🔍 in the header.
     *
     * **Set once, never toggled.** `floatingFilter` is what makes AG Grid lay out
     * the row under the headers, and flipping it would be a changed column
     * definition — a column rebuild, which is exactly what opening a search must
     * not cost. The row's *height* is what opens and closes it (see
     * `floatingFiltersHeight` below). `suppressFloatingFilterButton` because the
     * button beside a floating filter opens the column's own filter — the ad-hoc
     * layer this search is kept out of.
     */
    const withSearch = (out: ColDef<ScreenerRow>[]): ColDef<ScreenerRow>[] => {
      if (!searchable) return out;
      return out.map((def) =>
        (def.colId ?? def.field) !== idField
          ? def
          : {
              ...def,
              floatingFilter: true,
              floatingFilterComponent: SymbolSearchBox,
              floatingFilterComponentParams: searchParams,
              suppressFloatingFilterButton: true,
              headerComponentParams: { ...def.headerComponentParams, symbolSearch: search },
            },
      );
    };

    /**
     * The Add row's half of every column definition, applied last.
     *
     * **Every** column gets the selector, so no column's own renderer ever sees
     * the pinned row — see `AddRowCell`. Only the Symbol column becomes editable,
     * and only on that row: `editable` is a function of the node, so a click on
     * a real row's ticker still selects and charts exactly as before.
     *
     * Its keyboard rule replaces the grid-wide one on that column alone. The
     * other cells of the Add row keep letting a typed letter out to the host —
     * only the Symbol cell claims it, because only there does a letter mean
     * "this ticker".
     *
     * Nothing added here is read by `columnsFromDefs`, so none of it can reach
     * the saved config.
     */
    const withAddRow = (out: ColDef<ScreenerRow>[]): ColDef<ScreenerRow>[] => {
      if (!hasAddRow || !idField || !addCellParams) return out;
      const selector = (params: ICellRendererParams<ScreenerRow>) =>
        isAddRowNode(params.node) ? { component: AddRowCell, params: addCellParams } : undefined;
      return out.map((def) => {
        const decorated: ColDef<ScreenerRow> = { ...def, cellRendererSelector: selector };
        if ((def.colId ?? def.field) !== idField) return decorated;
        return {
          ...decorated,
          cellClassRules: {
            ...def.cellClassRules,
            "qf-add-row-hint": (params) => isAddRowNode(params.node),
          },
          editable: (params) => isAddRowNode(params.node),
          // Click `+ Add…` and type; a double-click to start is a gesture
          // nobody would guess for an empty row.
          singleClickEdit: true,
          cellEditor: AddRowEditor,
          cellEditorParams: addEditorParams,
          suppressKeyboardEvent: (params) =>
            isAddRowNode(params.node)
              ? addRowSuppressesKey(params.event, params.editing)
              : isPlainTypingKey(params.event),
        };
      });
    };

    const defs = buildColumnDefs({
      wholeNumber,
      fields: frame?.fields ?? [],
      columns: config.columns,
      resolveToken,
      meta,
      locale,
      categoryOf,
      // A column a server said is coming keeps its slot and draws pending.
      awaited: sources
        ? new Set([...sources].filter(([, source]) => source === "compute").map(([field]) => field))
        : undefined,
    });

    // **A column with no value must not render as a blank**, which reads as
    // broken — and under two-wave delivery a blank means either *not yet* or
    // *never*, which are not the same news. `buildUniverse` is the one place
    // that decides which, so the grid asks it rather than re-deriving the rule
    // beside `buildColumnDefs`; the two run over the same frame and config, so
    // they agree on which columns exist by construction.
    const states = new Map(
      buildUniverse({
        fields: frame?.fields ?? [],
        meta,
        columns: config.columns,
        sources,
      }).map((column) => [column.field, column.state] as const),
    );
    for (const def of defs) {
      const state = states.get(def.colId ?? def.field ?? "");
      if (state !== "pending" && state !== "unavailable") continue;
      // The renderer replaces the value, not the column: sort, filter, width
      // and header all stay exactly as configured, so the column keeps its slot
      // and nothing on screen moves when a value finally lands in it.
      def.cellRenderer = ColumnStateCell;
      def.cellRendererParams = state === "pending" ? PENDING_PARAMS : UNAVAILABLE_PARAMS;
      def.headerTooltip = def.headerTooltip
        ? `${def.headerTooltip} · ${STATE_TOOLTIP[state]}`
        : STATE_TOOLTIP[state];
    }

    if (!hasTrack || !idField) return withSearch(withAddRow(defs));

    const star: ColDef<ScreenerRow> = {
      colId: TRACK_LIST_FIELD,
      headerName: "",
      headerTooltip: "Track lists",
      // Pinned, so it stays with the identity block at any horizontal scroll —
      // but **not** `lockPosition`, which would force it to the far left of the
      // pinned region and drive it between the logo and the ticker. Those two
      // are read as a pair.
      pinned: "left",
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      resizable: false,
      // Sorting or filtering on a column with no value sorts nothing and looks
      // broken; suppressing the menu button is what stops the header offering it.
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      cellRenderer: TrackListCell,
      cellRendererParams: trackParams,
      cellStyle: { padding: 0 },
    };

    // After the identity block rather than at the front — `trackColumnIndex`
    // carries the reason and the two edge cases.
    const at = trackColumnIndex(defs);
    return withSearch(withAddRow([...defs.slice(0, at), star, ...defs.slice(at)]));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by value, see above
  }, [fieldsKey, columnsKey, sourcesKey, resolveToken, meta, locale, hasTrack, idField, trackParams, hasAddRow, addCellParams, addEditorParams, searchable, searchParams, search, categoryOf]);

  /** The defs as the grid gets them: runs sharing a band wrapped as one group. */
  const gridDefs = useMemo(
    () =>
      groupIntoBands(
        columnDefs,
        (def) => (def.context as { band?: Band | null } | undefined)?.band ?? null,
        BandHeader,
      ).map((def) => ("children" in def ? { ...def, headerClass: "qf-band" } : def)),
    [columnDefs],
  );

  /**
   * The Add row itself, or none.
   *
   * Only while the Symbol cell it lives in is shown: a screen that hides the
   * ticker would otherwise get a blank last row with nothing in it to click.
   */
  const addCellShown = useMemo(
    () =>
      hasAddRow &&
      idField != null &&
      columnDefs.some((def) => (def.colId ?? def.field) === idField && !def.hide),
    [hasAddRow, idField, columnDefs],
  );
  const pinnedBottomRowData = useMemo(
    () => (addCellShown ? [addRowData(addMessage)] : undefined),
    [addCellShown, addMessage],
  );

  /** Whatever ended the edit, the message about its text goes with it. */
  const handleEditingStopped = useCallback((event: CellEditingStoppedEvent<ScreenerRow>) => {
    if (isAddRowNode(event.node)) setAddMessage(null);
  }, []);

  /**
   * AG Grid calls this for pinned rows too, with `rowPinned` set — and a pinned
   * row whose id changed is a *new* row, whose cells (the open editor among
   * them) are torn down. So the Add row answers a constant rather than `""`,
   * which is what reading the symbol off its data would give.
   */
  const getRowId = useCallback(
    (params: GetRowIdParams<ScreenerRow>) =>
      params.rowPinned ? ADD_ROW_ID : rowId(params.data, idField ?? ""),
    [idField],
  );

  // ── snapshot -> transaction ────────────────────────────────────────────────
  //
  // **This is also the whole of wave 2.** A later push carrying only the
  // computed columns is merged into the held frames by `lib/column-wave.ts` and
  // arrives here as an ordinary new `frame` — so it is diffed by row id and
  // applied as updates, leaving selection, scroll and cell focus alone, and
  // leaving every column the push did not carry at the value it already had.
  // There is no second path and there must not be one: a wave that replaced
  // `rowData` would destroy the grid state on every compute tick, which is the
  // exact failure `getRowId` and `applyTransactionAsync` were adopted to avoid.

  useEffect(() => {
    const api = apiRef.current;
    if (!frame) return;
    const next = new Set(
      frame.fields.filter((f) => f.type === "number" && isEventColumn(frame.rows, f.name)).map((f) => f.name),
    );
    const prev = eventFields.current;
    const changed = [...new Set([...prev, ...next])].filter((f) => prev.has(f) !== next.has(f));
    if (changed.length === 0) return;
    eventFields.current = next;
    if (ready && api && !api.isDestroyed()) api.refreshCells({ columns: changed, force: true });
  }, [ready, frame]);

  useEffect(() => {
    const api = apiRef.current;
    if (!ready || !api || !frame || !idField) return;

    const { transaction, nextIds } = diffRows(presentIds.current, frame.rows, idField);

    // First push (or a frame swap): set the data outright. Diffing an empty grid
    // is just an add-everything transaction with extra steps.
    if (presentIds.current.size === 0) {
      api.setGridOption("rowData", frame.rows);
    } else {
      api.applyTransactionAsync(transaction);
    }
    presentIds.current = nextIds;
  }, [ready, frame, idField]);

  // A frame with a different identity column is a different dataset; drop the
  // diff baseline so the next push sets data outright instead of "updating"
  // rows whose ids mean something else entirely.
  useEffect(() => {
    presentIds.current = new Set();
  }, [idField]);

  // ── contract state -> grid ────────────────────────────────────────────────

  // The base filter is applied through AG Grid's *external* filter hooks rather
  // than translated into its filter model. `toGridModel` reports that it must
  // drop mixed AND/OR nesting and any third condition on a column, which means a
  // saved filter can be richer than the grid could redisplay — the worst failure
  // mode here, since it silently shows rows the filter excludes. Evaluating the
  // AST ourselves has no such ceiling.
  //
  // Held in a ref because AG Grid calls `doesExternalFilterPass` per row during
  // its own filter pass; a stale closure would filter against the previous AST.
  const baseFilter = useRef<ScreenerFilter | undefined>(config.filter);
  baseFilter.current = config.filter;

  /**
   * The live config, for callbacks that must not be re-created on every change.
   * `onColumnChanged` fires from AG Grid, not from React, so it needs the
   * *current* columns to merge into rather than whichever render it closed over.
   */
  const configRef = useRef(config);
  const onConfigChangeRef = useRef(onConfigChange);
  onConfigChangeRef.current = onConfigChange;
  configRef.current = config;

  /**
   * What is typed in the Symbol column's search, for the filter pass.
   *
   * A ref for `baseFilter`'s reason — AG Grid calls the pass per row, from its
   * own filter run — and fed by a subscription rather than a render, so a
   * keystroke costs one filter run and no render of this component.
   */
  const searchText = useRef(search.get().text);
  useEffect(
    () =>
      search.subscribe(() => {
        const text = search.get().text;
        if (text === searchText.current) return;
        searchText.current = text;
        // Source `api`: the grid's own bookkeeping to `handleFilterChanged`, so
        // a keystroke here never reaches `onAdHocChange`.
        apiRef.current?.onFilterChanged();
      }),
    [search],
  );

  const isExternalFilterPresent = useCallback(
    () => baseFilter.current != null || searchText.current.trim() !== "",
    [],
  );

  /**
   * Client-computed columns, held for the filter pass.
   *
   * A computed column has no field in the Arrow row — its value comes from a
   * `valueGetter` at render time — so filtering read `undefined` and matched
   * nothing. The column showed `true` in the grid and `eq true` found no rows,
   * which reads as the filter being broken rather than as the value not being
   * there.
   *
   * Only the client-evaluable ones: a `needsHistory` column is materialised
   * server-side and arrives as a real field, so it is already in the row.
   */
  const clientComputed = useRef<{ field: string; expression: ScreenerExpr }[]>([]);
  clientComputed.current = (config.columns ?? [])
    .filter((c) => c.computed && !needsHistory(c.computed.expression))
    .map((c) => ({ field: c.field, expression: c.computed!.expression }));

  const doesExternalFilterPass = useCallback((node: IRowNode<ScreenerRow>) => {
    if (!node.data) return true;
    // The search first: it is one string test, and it rules most rows out while
    // someone is typing. ANDed with the base filter, never folded into it.
    if (!matchesSymbolSearch(node.data, searchText.current)) return false;
    const computed = clientComputed.current;
    if (computed.length === 0) return matchesFilter(baseFilter.current, node.data);
    // Extended rather than mutated: the row is the grid's, and writing derived
    // values into it would leak them into anything else reading the data.
    const row: ScreenerRow = { ...node.data };
    for (const { field, expression } of computed) row[field] = evaluate(expression, node.data);
    return matchesFilter(baseFilter.current, row);
  }, []);

  useEffect(() => {
    const api = apiRef.current;
    if (!ready || !api) return;
    // External filters don't re-run on their own — without this the grid keeps
    // showing the previous filter's rows.
    api.onFilterChanged();
  }, [ready, config.filter]);

  const handleSelection = useCallback(() => {
    const api = apiRef.current;
    if (!api || !idField) return;
    const rows = api.getSelectedRows();
    const symbols = rows
      .map((row) => row[idField])
      .filter((value): value is string => typeof value === "string" && value !== "");

    onSelectedSymbols?.(symbols);

    // **Exactly one, not the first of several.** A grid that can multi-select
    // still means "show me this one" by a single selected row; the moment a
    // second row is ctrl-clicked the user is building a set, not asking for a
    // chart, and charting `rows[0]` there would make the chart lurch to whatever
    // they happened to click first and then stay there.
    if (!onSelectSymbol || symbols.length !== 1) return;
    // Only a real value: a rollup row's key is a sector, and a blank would
    // ask the chart to load nothing.
    onSelectSymbol(symbols[0]!);
  }, [onSelectSymbol, onSelectedSymbols, idField]);

  // The Symbol cell only, and only a real symbol: the Add row is pinned and a
  // rollup row's key is a sector.
  const handleCellOver = useCallback(
    (event: CellMouseOverEvent<ScreenerRow>) => {
      if (!onHoverSymbol || idField !== "symbol" || event.colDef.field !== idField) return;
      if (event.node.rowPinned) return;
      const symbol = event.value;
      const cell = (event.event?.target as Element | null)?.closest(".ag-cell");
      const grid = cell?.closest(".qf-screener-island");
      if (typeof symbol !== "string" || !symbol || !cell || !grid) return;
      onHoverSymbol(symbol, { cell: cell.getBoundingClientRect(), grid: grid.getBoundingClientRect() });
    },
    [onHoverSymbol, idField],
  );
  const handleCellOut = useCallback(
    (event: CellMouseOutEvent<ScreenerRow>) => {
      if (event.colDef.field === idField) onHoverSymbol?.(null, null);
    },
    [onHoverSymbol, idField],
  );

  // Repaint the star column when membership changes. See `TrackListBinding.version`
  // for why nothing else would: the column has no field, so its cells have no
  // value AG Grid could notice changing.
  useEffect(() => {
    const api = apiRef.current;
    if (!ready || !api || !hasTrack) return;
    api.refreshCells({ columns: [TRACK_LIST_FIELD], force: true });
  }, [ready, hasTrack, track?.version]);

  // ── keyboard row cursor ───────────────────────────────────────────────────

  /**
   * Move the cursor one displayed row and select what it lands on.
   *
   * Selection is the whole mechanism: `setSelected` fires `selectionChanged`,
   * which is `handleSelection`, which is the one path to `onSelectSymbol`. A
   * second call site for the callback would be a second place for the chart to
   * disagree with the highlight.
   *
   * Nothing here is config: the cursor is view state, so no `ScreenerConfigPatch`
   * is emitted and a held arrow key cannot turn into a workspace save per row.
   */
  const moveRowCursor = useCallback((step: RowStep) => {
    const api = apiRef.current;
    if (!api) return;

    // `rowIndex` is the row's position *as displayed* — after this grid's sort
    // and both filter layers — and is null while the row is filtered out.
    // `sourceRowIndex` is the position in the array the server sent, so stepping
    // by it would walk an order nobody is looking at.
    const current = api.getSelectedNodes()[0]?.rowIndex ?? null;
    const next = stepRowIndex(current, api.getDisplayedRowCount(), step);
    if (next == null) return;

    const node = api.getDisplayedRowAtIndex(next);
    if (!node) return;

    // Exclusively: this is a cursor, not an accumulating multi-select. Single
    // mode — which is what a grid with an `onSelectSymbol` is in — already
    // implies it; saying it anyway means walking a screen still *moves* the
    // selection rather than growing it if this ever runs on a multi-select grid.
    node.setSelected(true, true);
    api.ensureIndexVisible(next);

    // Drag AG Grid's focused cell along. Its own Tab, Home/End and Page keys
    // resume from the focused cell, so leaving it behind on the row the user
    // started from makes the next non-cursor keypress jump somewhere arbitrary —
    // and the focus ring would sit on one row while the highlight sits on another.
    const focused = api.getFocusedCell();
    if (focused) api.setFocusedCell(next, focused.column);
  }, []);

  /**
   * Bound to the island root, in the **capture** phase, and only when a listener
   * cares which symbol is picked — which is the detail frame; on a rollup tab
   * `ScreenerPanel` passes no `onSelectSymbol`, because a sector is not something
   * to chart, and AG Grid's own arrow-key cell focus is left to do its job there.
   *
   * Capture, rather than the usual bubble, so this decides before AG Grid's own
   * cell handler: the grid would move its focused cell on ↑/↓ and toggle
   * selection on Space, giving the user two cursors that drift apart. Both
   * `stopPropagation` (the grid must not also act) and `preventDefault` (the
   * browser must not scroll the grid body, which our `ensureIndexVisible` is
   * already doing, more precisely) are therefore deliberate.
   *
   * A native listener on the root rather than a React handler, because scoping is
   * what keeps text inputs safe: an event only reaches here when focus is already
   * inside the grid, so the panel's filter bar, column-picker search and DSL
   * editor — siblings of this element, not descendants — never see it.
   */
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !onSelectSymbol) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const step = rowCursorStep(event);
      if (step == null) return;
      event.preventDefault();
      event.stopPropagation();
      moveRowCursor(step);
    };

    root.addEventListener("keydown", onKeyDown, true);
    return () => root.removeEventListener("keydown", onKeyDown, true);
  }, [moveRowCursor, onSelectSymbol]);

  /**
   * Size columns to their content, for the ones nobody has sized by hand.
   *
   * A width nobody chose is a worse default than one measured from the data:
   * screens differ, and a fixed 90px is too wide for a flag and too narrow for
   * a company name. So a column with no configured `width` is measured, and one
   * with a width is left exactly as it is — resizing a column is a deliberate
   * act and must survive the next refresh.
   *
   * The result is not written back: `autosizeColumns` is a grid-originated
   * source, so `onColumnChanged` ignores it and the config keeps meaning "what
   * the user chose" rather than filling with measurements.
   */
  const autoSized = useRef<string>("");
  useEffect(() => {
    const api = apiRef.current;
    if (!ready || !api || !frame?.rows.length) return;

    // An image column is never measured — see `isMeasurable`.
    const configured = new Set(
      (config.columns ?? [])
        .filter((c) => typeof c.width === "number" || c.type === "image")
        .map((c) => c.field),
    );
    const measure = frame.fields.map((f) => f.name).filter((name) => !configured.has(name));
    // Nothing to measure forgets the last set measured. A user resize writes
    // *every* column's width back (`columnsFromDefs`), so this is the usual
    // state after one drag — and "Fit widths to data" then clears them all,
    // which is the very set measured on load. Kept, the key matched it and the
    // button did nothing.
    if (measure.length === 0) {
      autoSized.current = "";
      return;
    }

    // Once per column set, not per refresh: the server re-pushes a full snapshot
    // on every tick, and re-measuring each time would fight a resize mid-drag
    // and jitter the layout under the pointer.
    // **The longest value is part of the key**, not only the column set
    // (owner's screenshot, 2026-10-06: Rs, wRs, OBV and the Age columns clipped
    // to `-4…`, `14…` after Fit). Computed columns arrive in a second wave, so a
    // measure taken when Fit was clicked sized them to the placeholder; the set
    // of columns never changed, so nothing measured them again. A longer value
    // arriving now changes the key and re-measures. Character count stands in
    // for width — cheap over a frame, and it moves exactly when a wider value
    // lands. A column sized by hand is not in `measure`, so it is never touched.
    const longest = (field: string) => {
      let max = 0;
      for (const row of frame.rows) {
        const v = (row as Record<string, unknown>)[field];
        if (v === null || v === undefined) continue;
        const n = (typeof v === "number" ? v.toFixed(2) : String(v)).length;
        if (n > max) max = n;
      }
      return max;
    };
    const key = measure.map((f) => `${f}:${longest(f)}`).join("|");
    if (autoSized.current === key) return;
    // **Measured after the cells paint, not in this commit.** AG Grid sizes from
    // the rendered DOM, and every cell here is a React renderer (the Add row's
    // `cellRendererSelector`). Clearing widths rebuilds the column defs, so the
    // cells are redrawn and empty at this instant — measured now, every column
    // came out at its padding (`C…`, `1…`). Two frames lets React fill them.
    // The key is taken only once the measure has happened, so a cancelled
    // attempt is retried rather than remembered as done.
    let frameId = requestAnimationFrame(() => {
      frameId = requestAnimationFrame(() => {
        if (api.isDestroyed()) return;
        autoSized.current = key;
        sizeToData(api, measure);
      });
    });
    return () => cancelAnimationFrame(frameId);
  }, [ready, frame, config.columns]);

  /**
   * "Fit widths" from the editor (`lib/fit-request.ts`): measure every shown
   * column **now** and keep the result as the screen's widths. Only the detail
   * grid answers — the rollup tabs share the panel's id, and a second writer
   * would race it. Two frames first, for the reason the auto-measure above
   * gives: cells drawn by React renderers are empty in the commit that asked.
   */
  const isDetail = frame?.name === "detail" || rowIdField === "symbol";
  useEffect(() => {
    const id = config.id;
    if (!id || !isDetail) return;
    return onFitRequest(id, (mode) => {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const api = apiRef.current;
          if (!api || api.isDestroyed()) return;
          const shown = (api.getColumns() ?? [])
            .filter((c) => c.isVisible() && isMeasurable(c.getColDef()))
            .map((c) => c.getColId());
          // `names`: the header counts too, so no name is cut.
          api.autoSizeColumns(shown, mode === "data");
          // `autosizeColumns` is a grid-originated source, so the resize
          // handler ignores it — written back here, deliberately, as a choice.
          onConfigChangeRef.current?.({
            columns: columnsFromDefs(leafDefs(api.getColumnDefs() ?? []), configRef.current.columns ?? []),
          });
        }),
      );
    });
  }, [config.id, isDetail]);

  /**
   * **A logo is never measured** (owner's screenshot, 2026-10-30: the logo
   * column as wide as REGN's wordmark). The image is sized as a share of its
   * cell, and AG Grid measures a cell with no width to take a share of — so
   * every logo came out at its natural size and the widest wordmark set the
   * column. Fit then wrote that width back. An image column keeps its 32 px
   * start or whatever it was dragged to.
   */
  function isMeasurable(def: ColDef<ScreenerRow>): boolean {
    return def.cellRenderer !== ImageCell;
  }

  function sizeToData(api: GridApi<ScreenerRow>, measure: string[]) {
    // `skipHeader: true` — size to the **data**, not to the header.
    //
    // Including the header makes the longest word in the title the floor for the
    // whole column, which on this data is backwards: the headers are prose
    // ("Relative Strength Rank") and the values are two digits, so a screen of
    // numeric columns ends up sized by its labels and reads as mostly gutter.
    // The header now truncates with an ellipsis instead, which this feature's
    // doc already argues is the honest failure — the cut is visible, where an
    // invented abbreviation is not — and the full name is in the tooltip and in
    // the column picker's search either way.
    api.autoSizeColumns(measure, true);
  }

  // `modelUpdated` fires after filtering and after a row transaction, which is
  // exactly when the answer changes.
  const reportCounts = useCallback(() => {
    const api = apiRef.current;
    if (!api || !onRowCounts) return;
    let total = 0;
    api.forEachNode(() => {
      total += 1;
    });
    onRowCounts(api.getDisplayedRowCount(), total);
  }, [onRowCounts]);

  useEffect(() => {
    const api = apiRef.current;
    if (!ready || !api || !onExporter) return;
    // The columns an export carries: displayed, minus the star and image slots.
    const exportColumns = () =>
      api.getAllDisplayedColumns().filter((column) => {
        const def = column.getColDef();
        return column.getColId() !== TRACK_LIST_FIELD && def.cellRenderer !== ImageCell;
      });
    onExporter({
      rows: () => {
        const out: ScreenerRow[] = [];
        api.forEachNodeAfterFilterAndSort((node) => {
          if (node.data) out.push(node.data);
        });
        return out;
      },
      csv: () =>
        api.getDataAsCsv({
          skipPinnedBottom: true,
          columnKeys: exportColumns().map((column) => column.getColId()),
        }) ?? "",
      rowOf: (id) => {
        const rowNode = api.getRowNode(id);
        if (!rowNode?.data) return undefined;
        return exportColumns().map((column) => ({
          field: column.getColId(),
          header: api.getDisplayNameForColumn(column, "header"),
          value: api.getCellValue({ rowNode, colKey: column }),
        }));
      },
    });
    return () => onExporter(null);
  }, [ready, onExporter]);

  useEffect(() => {
    const api = apiRef.current;
    if (!ready || !api || !config.sort?.length) return;
    api.applyColumnState({
      state: config.sort.map((s, i) => ({
        colId: s.field,
        sort: s.dir,
        sortIndex: i,
      })),
      defaultState: { sort: null },
    });
  }, [ready, config.sort]);

  // ── grid -> contract state ────────────────────────────────────────────────

  const onGridReady = useCallback((event: GridReadyEvent<ScreenerRow>) => {
    apiRef.current = event.api;
    setReady(true);
  }, []);

  /**
   * The grid's own column funnels are the **ad-hoc layer**, composed on top of
   * the base filter with AND. They are deliberately *not* written back to
   * `config.filter`.
   *
   * Doing so was the previous behaviour and it silently destroys saved filters:
   * given `a and b and (c or d)`, one funnel nudge makes `fromGridModel` rebuild
   * a flat AND from only what the grid can see, deleting the OR group. So funnel
   * state stays transient, and `onMergeAdHoc` promotes it deliberately when the
   * user means to.
   */
  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent<ScreenerRow>) => {
      const api = apiRef.current;
      if (!api || !isUserOriginated(event.source)) return;
      onAdHocChange?.(fromGridModel(api.getFilterModel() as GridFilterModel));
    },
    [onAdHocChange],
  );

  const handleSortChanged = useCallback(
    (event: SortChangedEvent<ScreenerRow>) => {
      if (!onConfigChange || !isUserOriginated(event.source)) return;
      const sort: ScreenerSort[] = event.api
        .getColumnState()
        .filter((c) => c.sort)
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
        .map((c) => ({ field: c.colId, dir: c.sort as "asc" | "desc" }));
      onConfigChange({ sort });
    },
    [onConfigChange],
  );

  const handleColumnChanged = useCallback(
    (event: { source?: string; finished?: boolean }) => {
      const api = apiRef.current;
      if (!api || !onConfigChange || !isUserOriginated(event.source)) return;
      // A drag emits a resize event per pixel; only the last one is the result.
      if (event.finished === false) return;

      // Read back through getColumnDefs so moves, resizes and visibility toggles
      // all persist — including hidden columns, whose order must survive being
      // toggled off and on.
      //
      // The current config is passed so everything the grid does not model —
      // `computed`, `type`, `imageUrl`, `format`, `colorBy` — survives. Without
      // it, resizing a column erased the expression behind it.
      onConfigChange({
        columns: columnsFromDefs(
          // Bands are groups; the config is the leaves, in grid order.
          leafDefs(api.getColumnDefs() ?? []),
          configRef.current.columns ?? [],
        ),
      });
    },
    [onConfigChange],
  );

  const defaultColDef = useMemo<ColDef<ScreenerRow>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      // **No `minWidth`.** A floor here applied to every column, so a
      // three-letter header over 0/1 values could not be narrowed past 70px by
      // the drag handle or by the editor's Width field — the app refusing
      // rather than a setting doing its job. Width is measured from the data
      // (`autoSizeColumns` below) or typed by the user, and both of those are
      // better answers than one number that has to suit a flag and a company
      // name at once. Whatever floor AG Grid applies of its own accord is what
      // remains, and it is small enough not to be reached by real data.
      // **Let a typed character out of the grid.** AG Grid consumes every
      // printable key at a focused cell even when nothing is editable — see
      // `lib/typing-keys.ts` for the exact line that does it. Nothing here is
      // editable, and the host binds a letter to its symbol search, so without
      // this the shortcut died the moment anyone clicked a row. (The Add row's
      // Symbol cell is the one exception, and its column carries its own rule.)
      suppressKeyboardEvent: (params) => isPlainTypingKey(params.event),
    }),
    [],
  );

  // The `--ag-*` overrides must sit inline on the element carrying the theme
  // class: that class declares its own values on the same element, so an
  // ancestor's would lose. Inline wins, which is what makes the grid honour the
  // workspace theme rather than Quartz's built-in palette.
  const themeVars = useMemo(() => themeCssVars(resolveToken), [resolveToken]);

  return (
    <div
      ref={rootRef}
      className={`${themeClass} qf-screener-island${liveWindow ? ` ${LIVE_WINDOW_CLASS}` : ""}`}
      style={{ ...themeVars, width: "100%", height: autoHeight ? "auto" : "100%" }}
      // Read by `SEARCH_STYLE`: a closed search row is hidden as well as flat.
      data-symbol-search={searchOpen ? "open" : "closed"}
      onPaste={hasAddRow ? handlePaste : undefined}
    >
      <style>{BAND_STYLE}</style>
      {hasTrack ? <style>{TRACK_STYLE}</style> : null}
      {hasAddRow ? <style>{ADD_ROW_STYLE}</style> : null}
      {searchable ? <style>{SEARCH_STYLE}</style> : null}
      <AgGridReact<ScreenerRow>
        columnDefs={gridDefs}
        context={gridContext}
        domLayout={autoHeight ? "autoHeight" : "normal"}
        // Tight on purpose (owner, 2026-09-29): Quartz's default header is
        // ~48 px at this font, and with the band row above it the two cost more
        // height than the rows they label. 28 fits 13 px text and the 2 px live
        // line; 18 fits the band's 11 px. The Symbol search row, opened, takes
        // the header's height, so it shrinks with it.
        headerHeight={28}
        groupHeaderHeight={18}
        // AG Grid adds 20 px to every auto-sized column by default, for sort
        // icons in the header — but sizing skips the header (`autoSizeColumns`
        // above), so it was pure gutter on top of the 6 px cell padding a side
        // (`theme.ts`). Owner asked for very little margin, 2026-10-05: a
        // column is its widest value plus the 3 px cell padding a side.
        autoSizePadding={0}
        // `autoSizeColumns` measures **rendered** cells only, and AG Grid does
        // not render a column scrolled off to the right. With wide columns before
        // a Fit, everything past the panel's edge kept its old width — the left
        // half fitted, cPA onwards stayed wide and empty (owner, 2026-10-05).
        // Rows stay virtualised; only the visible columns are all drawn, and a
        // screen shows tens of them, not hundreds — hidden ones never render.
        suppressColumnVirtualisation

        defaultColDef={defaultColDef}
        // Replaces every header, so a non-daily column shows its period chip.
        components={HEADER_COMPONENTS}
        tooltipShowDelay={300}
        onModelUpdated={reportCounts}
        onCellMouseOver={onHoverSymbol ? handleCellOver : undefined}
        onCellMouseOut={onHoverSymbol ? handleCellOut : undefined}
        onSelectionChanged={
          onSelectSymbol || onSelectedSymbols ? handleSelection : undefined
        }
        getRowId={idField ? getRowId : undefined}
        onGridReady={onGridReady}
        isExternalFilterPresent={isExternalFilterPresent}
        doesExternalFilterPass={doesExternalFilterPass}
        onFilterChanged={handleFilterChanged}
        onSortChanged={handleSortChanged}
        onColumnMoved={handleColumnChanged}
        onColumnResized={handleColumnChanged}
        onColumnVisible={handleColumnChanged}
        onColumnPinned={handleColumnChanged}
        // The Add row. Pinned, so no sort, filter, count or selection ever
        // reaches it — see `lib/add-row.ts`.
        pinnedBottomRowData={pinnedBottomRowData}
        onCellEditingStopped={handleEditingStopped}
        // "Click away" ends the Add row's edit, as the approved behaviour has
        // it; without this, a click on the chart leaves the box open. Grid-wide
        // but harmless: that cell is the only editable one there is.
        stopEditingWhenCellsLoseFocus
        // The Symbol search's row: open is the header's height, closed is none.
        // A grid option, not a column property, so opening and closing it is a
        // header re-layout — no column definition changes, nothing is rebuilt,
        // and every width, position and sort stays exactly where it was.
        floatingFiltersHeight={searchOpen ? undefined : 0}
        // **Multiple, and a plain click still selects exactly one.** That is the
        // whole of the change: AG Grid's `multiple` mode already reserves
        // ctrl/shift for extending, and `rowMultiSelectWithClick` is left off,
        // so an ordinary click replaces the selection and still charts the
        // symbol through `handleSelection`. Turning it on would make every
        // single click accumulate, and the chart would stop following the grid.
        //
        // A grid with no listener for either callback keeps `multiple` too,
        // which it already had.
        rowSelection="multiple"
        rowMultiSelectWithClick={false}
        suppressCellFocus={false}
        animateRows={false}
      />
    </div>
  );
}
